import { UniqueEntityID } from '@/domain/core'
import { UseCase } from '@/domain/core/UseCase'
import { UserGeometry } from '@/domain/models/UserGeometry/UserGeometry'
import { Result } from '@/domain/protocols/Result'
import { PreparedUserGeometryData, USER_GEOMETRY_NAME_MAX_LENGTH } from '@/domain/protocols/UserGeometry'

import type { IUserGeometryRepository, UserGeometryFiles } from '@/data/UserGeometryRepository/IUserGeometryRepository'

import { createUniqueName } from '@/utils/createUniqueName'

export interface SaveUserGeometryRequest {
  userEditedName: string
  preparedData: PreparedUserGeometryData
}

// Persists a prepared geometry: the display name is made unique (case-insensitive,
// ` (1)` suffix) and every sync flag starts false so the geometry is pushed on the
// next sync pass. Metadata + both binaries are written transactionally by the repo.
export class SaveUserGeometryUseCase implements UseCase<SaveUserGeometryRequest, Promise<Result<void>>> {
  constructor(private readonly repository: IUserGeometryRepository) {}

  async execute(request: SaveUserGeometryRequest): Promise<Result<void>> {
    const { userEditedName, preparedData } = request

    try {
      const geometriesResult = await this.repository.getGeometries()
      if (geometriesResult.isFailure) {
        return Result.fail(geometriesResult.error)
      }

      // Fall back to the imported file's base name, and never clash with another
      // geometry (the geometry's own id is excluded so a re-save keeps its name).
      const nameToCheck = userEditedName || preparedData.baseName
      const existingNames = geometriesResult
        .getValue()
        .filter((geometry) => geometry.id.toString() !== preparedData.geometryId)
        .map((geometry) => geometry.name)
      const uniqueName = createUniqueName(nameToCheck, existingNames, {
        maxLength: USER_GEOMETRY_NAME_MAX_LENGTH
      })

      const now = new Date().toISOString()
      const geometry = UserGeometry.create(
        {
          name: uniqueName,
          originalFileName: preparedData.originalFileName,
          createdAt: now,
          updatedAt: now,
          featureCount: preparedData.featureCount,
          deleted: false,
          synced: false,
          originalSynced: false,
          processedSynced: false
        },
        new UniqueEntityID(preparedData.geometryId)
      )

      const files: UserGeometryFiles = {
        processed: preparedData.fgbBytes,
        original: {
          blob: preparedData.originalBytes,
          ext: preparedData.diagnostics.sourceFormat
        }
      }

      return await this.repository.saveGeometry(geometry, files)
    } catch (error) {
      return Result.fail(error)
    }
  }
}
